<template>
  <v-main>
    <v-container fluid>
      <v-row class="mb-2">
        <v-col cols="12">
          <v-card elevation="3">
            <!-- Page Header -->
            <page-header
              title="Blocked Doctors"
              :subtitle="`Doctors blocked by ${patientName || &quot;this patient&quot;} — read only`"
            >
              <template v-slot:actions>
                <div class="d-flex justify-end align-center">
                    <v-btn color="primary" outlined small @click="$router.push('/patients')">
                      <v-icon left x-small>mdi-arrow-left</v-icon>
                      Back to Patients
                    </v-btn>
                  </div>
              </template>
            </page-header>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-card elevation="3">
            <v-card-title class="py-2 px-4">
              <v-row class="w-100 ma-0" align="center" dense>
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20">mdi-account-cancel-outline</v-icon>
                    <span class="text-body-1 font-weight-medium">Blocked Doctors List</span>
                  </div>
                </v-col>
                <v-col cols="12" md="6" class="pa-0 text-right">
                  <v-chip color="primary" outlined x-small>
                    <v-icon left x-small>mdi-account-cancel</v-icon>
                    {{ blockedDoctors.length }} Blocked
                  </v-chip>
                </v-col>
              </v-row>
            </v-card-title>

            <v-divider></v-divider>

            <v-card-text class="pa-0">
              <div v-if="loading" class="text-center py-8">
                <v-progress-circular indeterminate color="primary" size="36"></v-progress-circular>
                <p class="mt-3 grey--text">Loading blocked doctors...</p>
              </div>

              <v-data-table
                v-else
                :headers="headers"
                :items="blockedDoctors"
                hide-default-footer
                :items-per-page="-1"
                class="blocked-doctors-table"
              >
                <!-- # -->
                <template #[`item.sr_no`]="{ index }">
                  <span class="font-weight-medium primary--text">{{ index + 1 }}</span>
                </template>

                <!-- Doctor -->
                <template #[`item.doctor`]="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar size="32" class="mr-2" color="grey lighten-2">
                      <v-img v-if="item.blocked_user && item.blocked_user.image_path" :src="item.blocked_user.image_path"></v-img>
                      <v-icon v-else size="18" color="grey darken-1">mdi-doctor</v-icon>
                    </v-avatar>
                    <span class="text-body-2 font-weight-medium">
                      {{ doctorName(item) }}
                    </span>
                  </div>
                </template>

                <!-- Contact -->
                <template #[`item.contact`]="{ item }">
                  <div class="text-caption">
                    <div v-if="item.blocked_user && item.blocked_user.phone" class="d-flex align-center">
                      <v-icon x-small class="mr-1 grey--text">mdi-phone</v-icon>{{ item.blocked_user.phone }}
                    </div>
                    <div v-if="item.blocked_user && item.blocked_user.email" class="d-flex align-center">
                      <v-icon x-small class="mr-1 grey--text">mdi-email</v-icon>{{ item.blocked_user.email }}
                    </div>
                    <span v-if="!item.blocked_user" class="grey--text">—</span>
                  </div>
                </template>

                <!-- Reason -->
                <template #[`item.reason`]="{ item }">
                  <span class="text-body-2">{{ item.reason || "—" }}</span>
                </template>

                <!-- Blocked At -->
                <template #[`item.created_at`]="{ item }">
                  <div class="text-caption">
                    <div class="font-weight-medium">{{ formatDate(item.created_at) }}</div>
                    <div class="grey--text">{{ formatTime(item.created_at) }}</div>
                  </div>
                </template>

                <!-- Empty state -->
                <template #no-data>
                  <div class="text-center py-10">
                    <v-icon size="60" color="grey lighten-3">mdi-account-check-outline</v-icon>
                    <h3 class="text-h6 grey--text mt-3 mb-2">No Blocked Doctors</h3>
                    <p class="grey--text">This patient hasn't blocked any doctors.</p>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";
export default {
  name: "BlockedDoctorsIndex",
  data() {
    return {
      blockedDoctors: [],
      loading: false,
      headers: [
        { text: "#",         value: "sr_no",     sortable: false, width: "50px", align: "center" },
        { text: "Doctor",    value: "doctor",    sortable: false, width: "220px" },
        { text: "Contact",   value: "contact",   sortable: false, width: "220px" },
        { text: "Reason",    value: "reason",    sortable: false },
        { text: "Blocked On", value: "created_at", sortable: false, width: "140px" },
      ],
    };
  },
  computed: {
    patientName() {
      return this.$route.query.patient_name || "";
    },
  },
  mounted() {
    this.getData();
  },
  methods: {
    async getData() {
      this.loading = true;
      try {
        const patientId = this.decryptIt(this.$route.params.id);
        const res = await axios.get(`/block_users?user_id=${patientId}`);
        this.blockedDoctors = res.data.data || [];
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    doctorName(item) {
      if (!item.blocked_user) return "Unknown Doctor";
      const name = `${item.blocked_user.first_name || ""} ${item.blocked_user.last_name || ""}`.trim();
      return name || "Unknown Doctor";
    },
    formatDate(dt) {
      if (!dt) return "—";
      return new Date(dt).toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" });
    },
    formatTime(dt) {
      if (!dt) return "";
      return new Date(dt).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
    },
  },
};
</script>

<style scoped>
.blocked-doctors-table >>> .v-data-table__wrapper table thead tr th {
  background: #f5f7fa;
  font-size: 0.75rem;
  font-weight: 600;
  color: #455a64;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}
.blocked-doctors-table >>> .v-data-table__wrapper table tbody tr:hover {
  background: #f8f9fe !important;
}
</style>
